/**
 * Sales Negotiation: where a character's pre-generated voice clips live.
 *
 * Clips are made by backend/scripts/generate-voice-clips.mjs, stored in the
 * Supabase "audios" bucket, and registered in data/audioManifest.json. This
 * module is the only place the browser turns a (character, slot) into a URL.
 *
 * audioUrl() returns null, never a guessed URL, when there is no clip for that
 * slot or the line has changed since the clip was made. Callers skip playback
 * on null, so a character without audio costs no 404 request.
 */
import manifest from './data/audioManifest.json';
import { lineForSlot, speakableText, textFingerprint } from './voiceLines.js';

export { openingLine } from './voiceLines.js';

// import.meta.env is Vite-only; optional chaining keeps this importable from Node tests.
const SUPABASE_URL = String(import.meta.env?.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '');
const BASE = SUPABASE_URL ? `${SUPABASE_URL}/storage/v1/object/public/${manifest.bucket}` : null;

export const audioUrl = (character, slot) => {
  const entry = manifest.clips?.[character?.id]?.[slot];
  if (!BASE || !entry?.path) return null;
  // Stale guard: the clip was generated from different text than the line now says.
  if (entry.fp !== textFingerprint(speakableText(lineForSlot(character, slot)))) return null;
  return `${BASE}/${entry.path}`;
};

/**
 * The `audioUrl` that /voice/respond sends when it spoke a live reply, or null when there is none or it is not a plain
 * http(s) URL. Live replies are not in the manifest (they are made on demand, and cached by the server), so unlike
 * audioUrl() there is no stale-text check: the server made this audio from exactly the text it sent alongside it.
 */
export const liveReplyUrl = (value) => (typeof value === 'string' && /^https?:\/\/\S+$/.test(value) ? value : null);

export const hasAudio = (character) =>
  Object.keys(manifest.clips?.[character?.id] ?? {}).length > 0;
